import React from 'react';
import {
  FaFileAlt,
  FaFileDownload,
  FaEye,
  FaTimes,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { Card, SectionTitle, Button, Spinner, EmptyState } from './Primitives';
import { formatBytes, formatDateTime } from '../utils/format';

/**
 * Step 6: risultati. Elenco dei file generati, anteprima inline e download ZIP.
 * Non vengono mostrati percorsi assoluti (il backend espone solo path relativi).
 */
export default function ResultsPanel({
  status,
  files,
  filesLoading,
  filesError,
  activeFileId,
  preview,
  previewLoading,
  previewError,
  onOpenPreview,
  onClosePreview,
  downloading,
  onDownload,
}) {
  const hasFiles = files.length > 0;

  return (
    <Card className="p-5">
      <SectionTitle
        icon={FaFileAlt}
        title="File generati"
        subtitle="Anteprima e download dell'output"
        action={
          <Button
            icon={downloading ? undefined : FaFileDownload}
            onClick={onDownload}
            disabled={!hasFiles || downloading}
            className="!px-3 !py-1.5 text-xs"
          >
            {downloading ? <Spinner className="h-4 w-4" /> : null}
            {downloading ? 'Preparazione…' : 'Scarica ZIP'}
          </Button>
        }
      />

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Elenco file */}
        <div>
          {filesLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
              <Spinner className="h-4 w-4" /> Caricamento file…
            </div>
          ) : filesError ? (
            <div className="flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <FaExclamationTriangle size={14} /> {filesError}
            </div>
          ) : !hasFiles ? (
            <EmptyState
              icon={FaFileAlt}
              title="Nessun file"
              hint={
                status === 'FAILED'
                  ? 'Il batch non ha prodotto output.'
                  : 'Nessun file generato è disponibile per questo batch.'
              }
            />
          ) : (
            <ul className="max-h-[28rem] space-y-1.5 overflow-y-auto pr-1">
              {files.map((f) => {
                const active = f.id === activeFileId;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => onOpenPreview(f.id)}
                      aria-pressed={active}
                      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        active
                          ? 'border-primary bg-primary/5 dark:bg-primary/10'
                          : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <FaFileAlt className="shrink-0 text-gray-400" size={14} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                          {f.name}
                        </span>
                        <span
                          className="block truncate text-xs text-gray-500 dark:text-gray-400"
                          title={f.path}
                        >
                          {f.path}
                        </span>
                      </span>
                      <span className="shrink-0 text-right text-xs text-gray-400">
                        <span className="block tabular-nums">{formatBytes(f.size)}</span>
                        <span className="block">{formatDateTime(f.modifiedAt)}</span>
                      </span>
                      <FaEye className="shrink-0 text-gray-300 group-hover:text-primary" size={13} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Anteprima */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700">
          {!activeFileId ? (
            <div className="flex h-full min-h-[16rem] items-center justify-center p-6">
              <EmptyState icon={FaEye} title="Anteprima" hint="Seleziona un file per visualizzarne il contenuto." />
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700">
                <span className="truncate text-xs font-medium text-gray-600 dark:text-gray-300">
                  {files.find((f) => f.id === activeFileId)?.name || 'Anteprima'}
                </span>
                <button
                  type="button"
                  onClick={onClosePreview}
                  aria-label="Chiudi anteprima"
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700"
                >
                  <FaTimes size={12} />
                </button>
              </div>
              <div className="flex-1 p-3">
                {previewLoading ? (
                  <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
                    <Spinner className="h-4 w-4" /> Caricamento anteprima…
                  </div>
                ) : previewError ? (
                  <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                    <FaExclamationTriangle size={13} /> {previewError}
                  </div>
                ) : (
                  <pre className="max-h-[24rem] overflow-auto rounded-lg bg-gray-900 p-3 text-xs leading-relaxed text-gray-100 dark:bg-black/50">
                    {preview?.content ?? ''}
                    {preview?.truncated && (
                      <span className="mt-2 block text-amber-400">
                        … contenuto troncato ({formatBytes(preview.size)})
                      </span>
                    )}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
